// src/oura/components/client/reportRows.ts
//
// The facts on a report row, shared by the Client tab's Reports card and the
// staff Documents page, so both describe a report the same way.

import { isInAppSnapshot, type ClientReportLink } from "@service/aeo/clientReview";
import { shortDateOrNull } from "../../lib/oura";

/** Newest first; a report with no readable date goes last, never first. */
export function byNewest(a: ClientReportLink, b: ClientReportLink): number {
    const x = Date.parse(a.date || "");
    const y = Date.parse(b.date || "");
    if (Number.isNaN(x) || Number.isNaN(y)) return Number.isNaN(x) && Number.isNaN(y) ? 0 : Number.isNaN(x) ? 1 : -1;
    return y - x;
}

/**
 * What kind of row this is, in staff words. The merchant's list does not say:
 * every row there is "a report we sent you". Staff are the ones who need to
 * tell their own upload apart from a rendered assessment or the quotation.
 */
export const KIND_LABEL: Record<NonNullable<ClientReportLink["kind"]>, string> = {
    upload: "Uploaded by us",
    snapshot: "Progress report",
    assessment: "AI check",
    document: "Engagement document",
    doc_approved: "Approved document",
    baseline_snapshot: "Made by Oura",
};

/**
 * The line under the title. Each part is a fact the payload carries, and a
 * part it does not carry is left out rather than guessed -- the uploader's
 * name is not on the payload today (BE #5324), so no row claims one.
 */
export function metaText(r: ClientReportLink): string {
    const pages = r.url && typeof r.pages === "number" && r.pages > 0 ? `${r.pages} ${r.pages === 1 ? "page" : "pages"}` : null;
    return [
        shortDateOrNull(r.date),
        r.kind ? KIND_LABEL[r.kind] : null,
        typeof r.termMonth === "number" ? `Month ${r.termMonth}` : null,
        // WHY there is no link, never a claim that it was emailed instead.
        r.url ? pages : isInAppSnapshot(r) ? "Opens here" : r.urlAbsent === "not_uploaded" ? "On our side only" : "No file yet",
    ]
        .filter(Boolean)
        .join(" · ");
}
